/**
 * Get environment variable from runtime config or build-time env.
 *
 * Priority:
 * 1. window.APP_CONFIG (runtime)
 * 2. import.meta.env (build-time)
 *
 * Throws an error by default if the variable is not found,
 * ensuring misconfigured deployments fail loudly.
 * Pass `{ throwOnMissing: false }` for variables that may not be set.
 *
 * @param key - The environment variable key (e.g., 'VITE_SOME_KEY')
 * @param options - { throwOnMissing: false } to return empty string instead of throwing
 * @returns The value, or empty string if throwOnMissing is false and not found
 */

declare global {
  interface Window {
    APP_CONFIG?: Record<string, string>;
  }
}

export function getEnvVar(key: string, options?: { throwOnMissing?: boolean }): string {
  // First, check runtime config (window.APP_CONFIG) - browser only
  if (typeof window !== 'undefined' && window.APP_CONFIG) {
    const runtimeValue = window.APP_CONFIG[key];
    if (runtimeValue !== undefined) {
      return String(runtimeValue);
    }
  }

  // Fall back to build-time env (import.meta.env) - Vite/browser only
  if (typeof import.meta !== 'undefined' && (import.meta as unknown as Record<string, unknown>).env) {
    const buildTimeValue = (import.meta as unknown as Record<string, unknown>).env as Record<
      string,
      string | undefined
    >;
    if (buildTimeValue[key] !== undefined && buildTimeValue[key] !== null) {
      return String(buildTimeValue[key]);
    }
  }

  // Fall back to process.env - Node.js/server
  if (typeof window === 'undefined' && typeof process !== 'undefined' && process.env) {
    const processEnvValue = process.env[key];
    if (processEnvValue !== undefined) {
      return String(processEnvValue);
    }
  }

  const message = `Missing environment variable: ${key}`;

  if (options?.throwOnMissing !== false) {
    throw new Error(message);
  }

  return '';
}
